'use client';

import { Toast as BaseToast } from '@base-ui-components/react/toast';

import { ToastItem } from './Toast';
import { toastManager } from './toast-manager';
import type { ToasterProps, ToastPosition } from './types';
import { cn } from '../../utils/cn';
import { useInheritedColorScheme } from '../../utils/use-inherited-color-scheme';

const positionClasses: Record<ToastPosition, string> = {
  'top-left': 'top-0 left-0 items-start',
  'top-right': 'top-0 right-0 items-end',
  'top-center': 'top-0 left-1/2 -translate-x-1/2 items-center',
  'bottom-left': 'bottom-0 left-0 items-start',
  'bottom-right': 'bottom-0 right-0 items-end',
  'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2 items-center',
};

function ToastViewport({
  position = 'bottom-right',
}: {
  position?: ToastPosition;
}) {
  const { toasts } = BaseToast.useToastManager();
  const { themeAttrs, probeRef } = useInheritedColorScheme();

  return (
    <>
      <span ref={probeRef} hidden aria-hidden="true" />
      <BaseToast.Portal>
        <BaseToast.Viewport
          className={cn(
            'fixed z-50 flex max-w-[420px] flex-col gap-2 p-4',
            positionClasses[position]
          )}
          {...themeAttrs}
        >
          {toasts.map(toast => (
            <ToastItem key={toast.id} toast={toast} />
          ))}
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </>
  );
}

export function Toaster({
  position = 'bottom-right',
  timeout = 5000,
  limit = 3,
}: ToasterProps = {}) {
  return (
    <BaseToast.Provider
      toastManager={toastManager}
      timeout={timeout}
      limit={limit}
    >
      <ToastViewport position={position} />
    </BaseToast.Provider>
  );
}
